import {UrlObject} from "url";

import {AutocompleteOnChange} from "@components/LongAutocomplete";
import NextImage from "@components/NextImage";
import {Btn, BtnProps} from "@components/V5Comp";
import {useTranslation} from "ni18n";
import React, {useState} from "react";

import useMediaQuery from "../../../useMediaQuery";
import CitiesAutocomplete from "../CitiesAutocomplete";

type Generic = {
  id: string;
  title: string;
  smallTitle: string;
  subtitle: string;
  icon: string;
  src: string;
  alt: string;
  href?: UrlObject;
};

type ButtonVariant = Generic & {
  btnProps: BtnProps;
  line1?: string;
  line2?: string;
};

type InputVariant = Generic & {
  onSearch: (cityAndState?: string) => void;
};

export type CoronavirusSection = ButtonVariant | InputVariant;

const isButtonVariant = (section: CoronavirusSection): section is ButtonVariant =>
  "btnProps" in section;

type Props = {
  section: CoronavirusSection;
};

const Section: React.FC<Props> = ({section}) => {
  const i18n = useTranslation();
  const [selected, setSelected] = useState<string>();

  const onValueChange: AutocompleteOnChange = (e, value) => {
    setSelected(value);
  };

  const isXs = useMediaQuery("xs");

  return (
    <div key={section.id} className="df minh110 pos-r fdc-md aic-md br3-md bs2-md maxw25-md">
      <span id={section.id} className="pos-a" style={{top: -60}} />
      <div className="fx2 pt10 pb5 pl2 p6-md pr4">
        <div className="df fdc maxw25">
          <h3 className="fs32 fs28-md mb4-md">{section.title}</h3>
          <p className="fs20 fs18-md mb5">{section.subtitle}</p>
          <div className="df aic fdrr jcfe fdc-md aifs-md">
            {isButtonVariant(section) && section.line1 && section.line2 && (
              <div className="df fdc jcc ml3 mb5-md ml0-md">
                <span className="font-csb fs18 mb1">{section.line1}</span>
                <span className="font-ir fs14">{section.line2}</span>
              </div>
            )}
            {isButtonVariant(section) && (
              <Btn {...section.btnProps} className="w100p-f-md focus-bsDarkBlue3" />
            )}
            {!isButtonVariant(section) && (
              <form
                className="pos-r w100p df aic"
                onSubmit={e => {
                  e.preventDefault();
                  section.onSearch(selected);
                }}
              >
                <div className="w100p">
                  <CitiesAutocomplete
                    label={isXs ? i18n.t("City or State") : i18n.t("Enter a city and state")}
                    onChange={onValueChange}
                    onLocate={() => {
                      section.onSearch();
                    }}
                    grayVariant
                    hideSearchIcon
                    clearOnEscape
                  />
                </div>
                <button
                  role="link"
                  type="submit"
                  aria-label={i18n.t("Search in this location.")}
                  className="brdn df tac p3 br50 ml2 t-100 bg-gray800 hover-bg-gray600 cp focus-bsDarkBlue3-hug"
                >
                  <span className="cIcon-search2 zIndex1 white fs26" />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      <div className="fx3 w100p-md minh58-md df">
        <div className="pos-r df fx">
          <NextImage
            src={section.src}
            alt={section.alt}
            layout="fill"
            className="br3 br3b-md"
            priority={section.id === "find-testing"}
          />
        </div>
      </div>
    </div>
  );
};

export default Section;
